import { useEffect, useState, useSyncExternalStore } from 'react'

export interface VisualViewportState {
  /**
   * Height the app should render into. Normally `visualViewport.height`,
   * corrected for the iOS-standalone stale-height bug (see `measure`).
   */
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

/**
 * True on iOS running as an installed (standalone) PWA. The stale
 * post-keyboard-close viewport-height bug this store works around is
 * specific to that context: in a regular Safari tab the URL bar absorbs the
 * offset and `visualViewport.height` recovers on its own. Computed once — it
 * can't change within a session.
 */
let _isIosStandaloneCache: boolean | null = null
function isIosStandalonePwa(): boolean {
  if (_isIosStandaloneCache !== null) return _isIosStandaloneCache
  try {
    const nav = window.navigator as Navigator & { standalone?: boolean }
    const ua = nav.userAgent || ''
    const isIos =
      /iP(hone|ad|od)/.test(ua) ||
      // iPadOS 13+ reports a desktop UA but is still iOS under the hood.
      (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)
    const standalone =
      (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      nav.standalone === true
    _isIosStandaloneCache = isIos && standalone
  } catch {
    _isIosStandaloneCache = false
  }
  return _isIosStandaloneCache
}

// ─── Single source of truth ──────────────────────────────────────────────────
//
// Everything below is module-level ON PURPOSE. The previous revision kept this
// state machine inside the hook body, so every `useVisualViewport()` call site
// got its OWN listener set, its OWN `fullHeight` ratchet, its OWN settle
// timers, and its OWN `useState`. Three call sites existed (Workspace page,
// MobileWorkspaceLayout, MobileTerminal), which meant two live, independently
// seeded ratchets driving different consumers — the page height from one, the
// keyboard-bar visibility and bottom padding from another. They were seeded at
// different mount times (the layout mounts only once the workspace is ready,
// possibly WHILE the keyboard is open) and could therefore disagree about what
// "full height" meant, with no mechanism to reconcile.
//
// One store, one measurement, one snapshot — every consumer renders from the
// same numbers in the same commit.

const INITIAL: VisualViewportState = {
  height: (typeof window !== 'undefined' && window.visualViewport?.height) || (typeof window !== 'undefined' ? window.innerHeight : 0),
  keyboardHeight: 0,
  isKeyboardOpen: false,
}

let state: VisualViewportState = INITIAL
const subscribers = new Set<() => void>()

/**
 * Tallest viewport height seen at the current viewport WIDTH = the true full
 * height for the current orientation/window size.
 *
 * Ratchet policy — this is the fix for the "app stuck ~60px short" report:
 * the value may RISE freely, but may only FALL when the viewport width
 * changes. A keyboard never changes the width, so no keyboard transition can
 * lower the ratchet. The old code re-baselined it on any window `resize` where
 * nothing was focused, which meant a resize arriving mid-keyboard-close (or a
 * synthetic one dispatched deliberately during a view switch) latched the
 * ratchet to the keyboard-open or stale-short height. Once latched low, the
 * iOS stale-height override below could never fire again — precisely in the
 * situation it exists to correct — and the app rendered permanently short.
 */
let fullHeight = INITIAL.height
let ratchetWidth = typeof window !== 'undefined' ? window.innerWidth : 0

let wasKeyboardOpen = false
/**
 * Epoch ms of the last measurement that saw the keyboard open. Gates the
 * iOS-standalone stale-height override below: that bug is strictly a
 * POST-KEYBOARD-CLOSE artifact, so the override may only hold the layout at
 * the cached full height for a bounded window after real keyboard use.
 * Without the gate, a full-height cache that is wrong (bad early reading, or
 * iOS legitimately shrinking the viewport later) pins the layout TALLER than
 * the screen forever — the composer renders half off-screen and nothing can
 * correct it, since the ratchet only falls on a width change.
 */
let lastKeyboardSeenAt = 0
const STALE_OVERRIDE_HOLD_MS = 60_000
/**
 * Epoch ms of the last focus-in on an editable element, cleared the moment a
 * measurement actually SEES the keyboard open. While set (and fresh), the
 * viewport is in the keyboard-OPEN phase: iOS may pan the page before the
 * height shrink crosses the keyboard threshold, and fighting that pan with
 * `scrollTo(0,0)` races Safari's own focus handling — the visible layout
 * jump. Once the open completes (or the window expires — e.g. a hardware
 * keyboard meant no soft keyboard ever appeared), pans are residual again
 * and cleanup may proceed, even if the editable still holds focus (the iOS
 * "hide keyboard" button closes the keyboard WITHOUT blurring).
 */
let lastFocusInAt = 0
const FOCUS_OPEN_PHASE_MS = 1200
let settleTimers: number[] = []
let unpanTimers: number[] = []
/** Re-entrancy guard: an unpan attempt re-measures, and a measure can ask for
 *  an unpan. Without this they would call each other without bound. */
let unpanning = false
let activeCount = 0
let detach: (() => void) | null = null

function clearTimers(list: number[]): number[] {
  list.forEach((t) => window.clearTimeout(t))
  return []
}

function emit() {
  subscribers.forEach((fn) => fn())
}

/**
 * Collapse a visual-viewport pan — whenever one appears, keyboard up OR down.
 *
 * The pan is never something this app wants:
 * - While the keyboard OPENS, iOS pans to reveal the focused input — but our
 *   fixed shell already resizes to keep the composer visible, so the pan only
 *   produces the visible transient the user reported: the browser pans the
 *   page instantly in the compositor, and our compensating translate follows
 *   frames later — header and top buttons jump, then "slide" back into place.
 *   Collapsing the pan at the source keeps the chrome pinned; only the
 *   container height changes (the keyboard "pushes the bottom up").
 * - After the keyboard CLOSES, iOS does not always restore `offsetTop` to 0 —
 *   the pan can survive indefinitely (nothing in the app scrolls the window),
 *   leaving the composer high with dead space beneath it.
 *
 * `window.scrollTo(0, 0)` is what collapses it, retried across the keyboard
 * animation and re-measured after each attempt so `offsetTop` reflects
 * reality. If iOS refuses (or a webview rejects programmatic scrolls), the
 * verbatim `offsetTop` keeps the container translate compensating — glued
 * beats jumping.
 *
 * The one pan we must never touch: a user pinch-zoom (scale > 1) legitimately
 * pans the visual viewport; fighting it would yank the zoomed view around.
 */
function runUnpanAttempt() {
  const vv = window.visualViewport
  if (!vv) return
  // Post-close cleanup ONLY (the Telegram Web A pattern: never fight the pan
  // while the keyboard is up/transitioning — device diagnostics showed iOS
  // doesn't even report the open-phase pan, and open-phase scrollTo just
  // races Safari's own focus handling). Never touch a pinch-zoom pan.
  if (state.isKeyboardOpen || (vv.scale || 1) > 1.01 || (vv.offsetTop || 0) <= 0) return
  // Keyboard-OPEN phase (focus landed, height shrink not yet past the
  // threshold): the pan belongs to Safari's focus handling — leave it alone.
  if (lastFocusInAt !== 0 && Date.now() - lastFocusInAt < FOCUS_OPEN_PHASE_MS) return
  unpanning = true
  try {
    try {
      window.scrollTo(0, 0)
    } catch {
      // Some embedded webviews reject programmatic scrolls — nothing to do.
    }
    // Re-read after the scroll so `offsetTop` reflects whether the pan
    // actually collapsed, rather than our hope that it did.
    measure()
  } finally {
    unpanning = false
  }
}

/** Attempt immediately, then across the tail of the keyboard animation. */
const UNPAN_DELAYS_MS = [120, 350, 700]

function scheduleUnpan() {
  if (unpanning) return
  unpanTimers = clearTimers(unpanTimers)
  runUnpanAttempt()
  UNPAN_DELAYS_MS.forEach((ms) => {
    unpanTimers.push(window.setTimeout(runUnpanAttempt, ms))
  })
}

/**
 * True when the currently focused element could be holding the soft keyboard
 * open. An iframe counts: it may contain its own editable focus (e.g. the
 * Xpra browser panel) that this document cannot see into.
 */
function editableElementHasFocus(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'IFRAME' ||
    el.isContentEditable === true
  )
}

function measure() {
  const viewport = window.visualViewport
  if (!viewport) return

  const layoutHeight = window.innerHeight
  // Whole pixels only. iOS reports fractional visualViewport heights that
  // wobble in the third decimal across events (caret scrolls, QuickType bar
  // animation frames); compared raw with `!==` they made every wobble an
  // emission, and each emission re-renders the entire workspace tree — the
  // "flickers while I type" report. Nothing can usefully render a third of a
  // CSS pixel anyway.
  const rawVisibleHeight = Math.round(viewport.height)
  const heightDiff = layoutHeight - rawVisibleHeight

  // Detect the keyboard by height shrink only, never by offsetTop: a pan can
  // outlive the keyboard, and using it for detection pinned isKeyboardOpen
  // true until a manual scroll. A genuine soft keyboard is always > 100px.
  //
  // AND require an editable element to actually hold focus: a soft keyboard
  // cannot be open when nothing editable is focused. iOS sometimes keeps
  // reporting the keyboard-shrunk visualViewport.height long after the
  // keyboard closed; trusting geometry alone left the store stuck in
  // keyboard-open state and the whole layout rendered a keyboard-height
  // short — dead white space under the panels.
  const editing = editableElementHasFocus()
  const isKeyboardOpen = heightDiff > 100 && editing
  const keyboardHeight = isKeyboardOpen ? Math.max(heightDiff, 0) : 0
  if (isKeyboardOpen) {
    lastKeyboardSeenAt = Date.now()
    // The open phase completed — later pans are residual, not Safari's focus
    // handling, so the unpan gate keyed on this must release.
    lastFocusInAt = 0
  }

  // Keyboard-shaped shrink with no keyboard possible (and no pinch zoom):
  // the visual-viewport reading is stale. The layout viewport can't be
  // keyboard-shrunk when no keyboard exists, so it is the better estimate.
  const visibleHeight =
    !isKeyboardOpen && heightDiff > 100 && (viewport.scale || 1) <= 1.01
      ? layoutHeight
      : rawVisibleHeight

  // Ratchet rises only, and only on a clean closed-keyboard measurement
  // (never from the stale-reading substitution above).
  if (!isKeyboardOpen && heightDiff <= 100 && visibleHeight > fullHeight) {
    fullHeight = visibleHeight
  }

  // iOS PWA bug: after a swipe-down keyboard dismiss, both window.innerHeight
  // and visualViewport.height stay pinned ~60px below the true full height
  // until a real touch-scroll forces iOS to recompute. There is nothing to
  // scroll (the layout is exactly viewport-height), so we ignore the stale
  // reading and use the cached true full height instead.
  //
  // Gated to iOS standalone PWA: elsewhere a sub-full closed-keyboard height
  // is legitimate (URL-bar show/hide in a Safari tab, iPad split view), and
  // overriding it would clip content. The 8px tolerance skips sub-pixel noise.
  //
  // Also gated to a bounded window after real keyboard use: the pin is
  // strictly a post-keyboard-close artifact. Outside that window a sub-full
  // reading is reality (however it came about) — accept it and re-baseline
  // the ratchet, or a wrong cached full height would hold the layout taller
  // than the screen indefinitely, clipping the composer at the bottom.
  let effectiveHeight = visibleHeight
  if (isIosStandalonePwa() && !isKeyboardOpen && visibleHeight < fullHeight - 8) {
    if (lastKeyboardSeenAt !== 0 && Date.now() - lastKeyboardSeenAt < STALE_OVERRIDE_HOLD_MS) {
      effectiveHeight = fullHeight
    } else {
      fullHeight = visibleHeight
    }
  }

  // Local only — never stored on `state` (no public consumer reads it; see
  // `VisualViewportState`). Still drives the unpan trigger below, and
  // `runUnpanAttempt` separately re-reads the live `viewport.offsetTop` itself.
  const offsetTop = Math.max(0, Math.round(viewport.offsetTop || 0))

  // COMMIT FIRST, react to transitions after. `runUnpanAttempt` reads
  // `state.isKeyboardOpen` to decide whether a pan is residual or legitimate;
  // scheduling an unpan before the commit would show it the PREVIOUS
  // (keyboard-open) snapshot on exactly the close transition that needs it,
  // and it would bail every time.
  const changed =
    state.height !== effectiveHeight ||
    state.keyboardHeight !== keyboardHeight ||
    state.isKeyboardOpen !== isKeyboardOpen

  if (changed) {
    // iOS fires resize/scroll on every keyboard-animation frame; only notify
    // when something actually moved so consumers don't re-render per frame.
    // An offsetTop-only change (e.g. residual pan collapsing) must NOT emit —
    // no public field reflects it, so notifying subscribers here would just
    // be a wasted re-render.
    state = { height: effectiveHeight, keyboardHeight, isKeyboardOpen }
    emit()
  }

  const justClosed = wasKeyboardOpen && !isKeyboardOpen
  wasKeyboardOpen = isKeyboardOpen

  // Keyboard just CLOSED: iOS keeps reporting transitional geometry for
  // several hundred ms (worse when toggled rapidly). Re-measure after the
  // animation settles so the last word is real geometry.
  if (justClosed) {
    settleTimers = clearTimers(settleTimers)
    settleTimers = [window.setTimeout(measure, 350), window.setTimeout(measure, 900)]
  }

  // Residual pan cleanup with the keyboard down — after a close, or already
  // present when resuming from the background. Open-phase pans are prevented
  // at the source instead (document lock + cc-focus-blink), never fought.
  if (!isKeyboardOpen && offsetTop > 0) {
    scheduleUnpan()
  }
}

/**
 * Imperative re-measure for callers that changed the layout in a way the
 * browser won't report (e.g. unmounting the element that held keyboard focus:
 * no blur and no resize fires, the node simply disappears).
 *
 * Deliberately NOT a synthetic `window.resize` dispatch — that is how the
 * ratchet used to get poisoned, since the window-resize path re-baselines the
 * full height. This re-measures without touching the ratchet, and settles
 * across the keyboard-close animation.
 */
export function requestViewportRemeasure(): void {
  if (typeof window === 'undefined' || !window.visualViewport) return
  measure()
  settleTimers = clearTimers(settleTimers)
  settleTimers = [
    window.setTimeout(measure, 200),
    window.setTimeout(measure, 500),
    window.setTimeout(measure, 900),
  ]
  scheduleUnpan()
}

function attach(): () => void {
  const viewport = window.visualViewport

  if (!viewport) {
    // Fallback for browsers without the visualViewport API: focus heuristics.
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        window.setTimeout(() => {
          state = {
            ...state,
            isKeyboardOpen: true,
            keyboardHeight: 300,
            height: window.innerHeight - 300,
          }
          emit()
        }, 300)
      }
    }
    const handleBlur = () => {
      state = { height: window.innerHeight, keyboardHeight: 0, isKeyboardOpen: false }
      emit()
    }
    document.addEventListener('focusin', handleFocus)
    document.addEventListener('focusout', handleBlur)
    return () => {
      document.removeEventListener('focusin', handleFocus)
      document.removeEventListener('focusout', handleBlur)
    }
  }

  viewport.addEventListener('resize', measure)
  viewport.addEventListener('scroll', measure)

  // Window resize = orientation / window-size change. The keyboard never
  // fires this on iOS. Re-baseline the ratchet DOWNWARD only when the width
  // actually changed (rotation, split view, Stage Manager) — see the ratchet
  // policy note above for why an unconditional re-baseline was harmful.
  const onWindowResize = () => {
    const width = window.innerWidth
    if (width !== ratchetWidth) {
      ratchetWidth = width
      fullHeight = window.visualViewport?.height ?? window.innerHeight
    }
    measure()
  }
  window.addEventListener('resize', onWindowResize)

  // Counterpart safety net to onFocusOut below: focus ENTERING an editable
  // element can change keyboard truth WITHOUT any visualViewport event. The
  // stuck case this cures: a measurement taken while the keyboard was up but
  // focus had bounced off the textarea (WS blip disabling it, a dropdown tap)
  // reads as "keyboard-shaped shrink with no editor" → the stale-reading
  // substitution renders FULL height → composer sits behind the keyboard.
  // Re-tapping the textarea then fires NO viewport resize (the keyboard never
  // moved), so without this listener nothing ever re-measured and the state
  // stuck until the keyboard was manually dismissed. Measure now and across
  // the open animation; also arms the open-phase unpan gate.
  const onFocusIn = () => {
    if (!editableElementHasFocus()) return
    lastFocusInAt = Date.now()
    measure()
    settleTimers = clearTimers(settleTimers)
    settleTimers = [
      window.setTimeout(measure, 100),
      window.setTimeout(measure, 350),
      window.setTimeout(measure, 900),
    ]
  }
  document.addEventListener('focusin', onFocusIn)

  let focusOutTimer: number | null = null
  // Safety net: when focus leaves the page (or moves to a non-editable
  // element) the keyboard can't be open regardless of what visualViewport
  // currently reports. Defer so iOS finishes its close animation first.
  const onFocusOut = () => {
    if (focusOutTimer !== null) window.clearTimeout(focusOutTimer)
    focusOutTimer = window.setTimeout(() => {
      focusOutTimer = null
      const active = document.activeElement as HTMLElement | null
      const stillEditing =
        !!active &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable)
      if (stillEditing) return
      measure()
      scheduleUnpan()
    }, 300)
  }
  document.addEventListener('focusout', onFocusOut)

  // Returning from the background can leave the tracked geometry stale: the
  // keyboard closed while the page was frozen and its resize never reached us.
  const resumeTimers: number[] = []
  const remeasure = () => {
    measure()
    resumeTimers.push(window.setTimeout(measure, 350))
    resumeTimers.push(window.setTimeout(measure, 1000))
    scheduleUnpan()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') remeasure()
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pageshow', remeasure)

  measure()

  return () => {
    viewport.removeEventListener('resize', measure)
    viewport.removeEventListener('scroll', measure)
    window.removeEventListener('resize', onWindowResize)
    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('focusout', onFocusOut)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pageshow', remeasure)
    resumeTimers.forEach((t) => window.clearTimeout(t))
    settleTimers = clearTimers(settleTimers)
    unpanTimers = clearTimers(unpanTimers)
    if (focusOutTimer !== null) window.clearTimeout(focusOutTimer)
  }
}

function activate(): () => void {
  activeCount += 1
  if (activeCount === 1) detach = attach()
  return () => {
    activeCount -= 1
    if (activeCount === 0 && detach) {
      detach()
      detach = null
    }
  }
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb)
  return () => {
    subscribers.delete(cb)
  }
}

function getSnapshot(): VisualViewportState {
  return state
}

/**
 * Track the visual viewport height and mobile keyboard presence.
 *
 * All call sites share ONE underlying measurement store, so the page height,
 * the keyboard bar and any other consumer can never disagree about the
 * viewport — which was the structural cause of the mobile layout drifting into
 * a shifted state. `useSyncExternalStore` guarantees every subscriber reads the
 * same snapshot in the same commit.
 *
 * `enabled` (default true) gates the underlying listeners; pass the page's
 * `isMobile` so desktop attaches nothing. Listeners are attached while at
 * least one enabled consumer is mounted.
 */
export function useVisualViewport(enabled: boolean = true): VisualViewportState {
  useEffect(() => {
    if (!enabled) return
    return activate()
  }, [enabled])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Should the layout be driven by the measured pixel height instead of CSS?
 *
 * True while the keyboard is open, and for a short settle window after it
 * closes. Outside that window the answer is no — CSS `dvh` is the better
 * authority, because it is the only one that agrees with the screen.
 *
 * Why this exists: `visualViewport.height` is the VISIBLE viewport, and on iOS
 * that is not the same as the layout viewport. With a translucent status bar
 * the layout viewport (what `dvh` resolves to) spans the whole screen, while
 * the visible viewport is shorter by the status bar in PORTRAIT — and equal in
 * LANDSCAPE, where iOS hides the status bar. Pinning the shell to the measured
 * value therefore left a permanent dead band along the bottom in portrait only,
 * present from first load with no keyboard ever involved. The dashboard, which
 * sizes with plain CSS, never had it.
 *
 * The measured value is still exactly right for the keyboard, which is not
 * browser chrome and which `dvh` cannot express — and for a moment afterwards,
 * since `dvh` lags the real height by a few frames on close. So: JS while it
 * matters, CSS the rest of the time.
 */
export function useMeasuredHeightPreferred(settleMs = 700): boolean {
  const { isKeyboardOpen } = useVisualViewport()
  const [preferred, setPreferred] = useState(isKeyboardOpen)

  useEffect(() => {
    if (isKeyboardOpen) {
      setPreferred(true)
      return
    }
    const timer = window.setTimeout(() => setPreferred(false), settleMs)
    return () => window.clearTimeout(timer)
  }, [isKeyboardOpen, settleMs])

  return preferred
}
